import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client.js";
import { colors, font } from "../utils/theme.js";
import { inputStyle, primaryBtn, ghostBtn } from "../clients/ui.js";
import { searchContacts, clientsByContact } from "./crmLogic.js";
import ContactForm from "./ContactForm.jsx";
import ContactCard from "./ContactCard.jsx";
import ImportCsv from "./ImportCsv.jsx";
import Modal from "./Modal.jsx";

const NONE = "__none";

// The shared Sawtooth contact book (spec 2026-11-03): everyone the team has met, searchable,
// with a chip for each client that has them in its CRM.
export default function ContactsPage({ users }) {
  const [contacts, setContacts] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [clients, setClients] = useState([]);
  const [status, setStatus] = useState("loading");
  const [query, setQuery] = useState("");
  const [org, setOrg] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [toast, setToast] = useState(null);
  const flash = (type, text) => { setToast({ type, text }); setTimeout(() => setToast((t) => (t && t.text === text ? null : t)), 5000); };

  const loadedRef = useRef(false);
  const load = useCallback(async () => {
    const [cs, ts, cl] = await Promise.all([api.contacts.listAll(), api.crmTracks.listAll(), api.clients.list().catch(() => null)]);
    // A failed refresh keeps the list on screen; only a failed first load shows the error.
    if (!cs || !ts) { if (loadedRef.current) flash("error", "Couldn’t refresh contacts."); else setStatus("error"); return; }
    loadedRef.current = true;
    setContacts(cs); setTracks(ts); setClients(cl || []); setStatus("ready");
  }, []);
  useEffect(() => { load(); }, [load]);

  const byContact = useMemo(() => clientsByContact(tracks), [tracks]);
  const clientName = (id) => clients.find((c) => c.id === id)?.name || "Unknown client";
  const orgs = useMemo(() => [...new Set(contacts.map((c) => c.organization).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [contacts]);
  const shown = useMemo(() => searchContacts(contacts, query)
    .filter((c) => !org || c.organization === org)
    .filter((c) => {
      if (!clientFilter) return true;
      const ids = byContact.get(c.id) || [];
      return clientFilter === NONE ? ids.length === 0 : ids.includes(clientFilter);
    })
    .sort((a, b) => a.name.localeCompare(b.name)), [contacts, query, org, clientFilter, byContact]);
  const open = contacts.find((c) => c.id === openId);

  const th = { textAlign: "left", color: colors.textDim, fontWeight: 500, fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.5, padding: 8, borderBottom: `1px solid ${colors.border}` };
  const td = { padding: "9px 8px", borderBottom: `1px solid ${colors.border}`, fontSize: 13, color: colors.text, verticalAlign: "middle" };
  const chip = { display: "inline-block", fontSize: 11.5, padding: "2px 8px", borderRadius: 10, background: colors.accentDim, color: "#93c5fd", margin: "1px 4px 1px 0", whiteSpace: "nowrap" };
  const tag = { display: "inline-block", fontSize: 11, padding: "1px 7px", borderRadius: 4, background: colors.surfaceAlt, color: colors.textMuted, margin: "1px 4px 1px 0" };
  const select = { ...inputStyle, width: "auto" };

  if (status === "loading") return <div style={{ color: colors.textDim, fontSize: 14 }}>Loading contacts…</div>;
  if (status === "error") return <div style={{ color: colors.danger, fontSize: 14 }}>Couldn’t load the contact book. <button style={{ ...ghostBtn, marginLeft: 8 }} onClick={load}>Retry</button></div>;
  return (
    <div style={{ fontFamily: font }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
        <input style={{ ...inputStyle, width: 300 }} placeholder="Search name, organization, role, base, expertise…" value={query} onChange={(e) => setQuery(e.target.value)} />
        <select aria-label="Organization filter" style={select} value={org} onChange={(e) => setOrg(e.target.value)}>
          <option value="">All organizations</option>
          {orgs.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <select aria-label="Client filter" style={select} value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
          <option value="">Any client</option>
          <option value={NONE}>Not linked to any client</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: colors.textDim }}>{shown.length} of {contacts.length}</span>
        <button style={ghostBtn} onClick={() => setImporting(true)}>Import CSV</button>
        <button style={primaryBtn} onClick={() => setAdding(true)}>+ Add contact</button>
      </div>
      {contacts.length === 0 ? (
        <div style={{ color: colors.textDim, fontSize: 14, padding: "30px 0", textAlign: "center" }}>No contacts yet. Add one, or import a spreadsheet.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["Name", "Organization", "Role", "Base", "Expertise", "Email", "Phone", "Clients"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {shown.map((c) => {
                const ids = byContact.get(c.id) || [];
                return (
                  <tr key={c.id} onClick={() => setOpenId(c.id)} style={{ cursor: "pointer" }}>
                    <td style={td}><b>{c.name}</b>{c.source && <div style={{ fontSize: 11.5, color: colors.textDim }}>{c.source}</div>}</td>
                    <td style={td}>{c.organization}</td>
                    <td style={td}>{c.role}</td>
                    <td style={td}>{c.base}</td>
                    <td style={td}>{(c.expertise || []).map((t) => <span key={t} style={tag}>{t}</span>)}</td>
                    <td style={{ ...td, color: colors.textMuted }}>{c.email}</td>
                    <td style={{ ...td, color: colors.textMuted, whiteSpace: "nowrap" }}>{c.phone}</td>
                    <td style={td}>{ids.length ? ids.map((id) => <span key={id} style={chip}>{clientName(id)}</span>) : <span style={{ fontSize: 11.5, color: colors.textDim }}>— not linked</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {adding && (
        <Modal title="Add contact" onClose={() => setAdding(false)}>
          <ContactForm contacts={contacts} mode="book" onCancel={() => setAdding(false)}
            onCreated={(c) => { setAdding(false); flash("success", `Added ${c.name}`); load(); }}
            onOpen={(id) => { setAdding(false); setOpenId(id); }} />
        </Modal>
      )}
      {importing && (
        <ImportCsv contacts={contacts} users={users || []} onClose={() => setImporting(false)}
          onDone={(summary) => { setImporting(false); flash("success", `Import done: ${summary}`); load(); }} />
      )}
      {open && (
        <ContactCard key={open.id} contact={open} tracks={tracks.filter((t) => t.contactId === open.id)} clients={clients} users={users || []}
          flash={flash} onChanged={load} onClose={() => setOpenId(null)} />
      )}
      {toast && (
        <div role="status" style={{ position: "fixed", bottom: 24, right: 24, zIndex: 1400, padding: "10px 16px", borderRadius: 8, fontSize: 13, background: toast.type === "error" ? colors.danger : colors.success, color: "#fff" }}>{toast.text}</div>
      )}
    </div>
  );
}
